import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import NavBar from "./NavBar";
import "../styles/styles.css";
import "../styles/nav.css";
import { getPatientById, patchPatient, getCompanies } from "../services/api";
import { toast } from 'react-toastify';
import {formatRut} from "../utils/rut";
import {formatPhone} from "../utils/phone";

const EditPatient = () => {
  const { pacienteId } = useParams();
  const [empresas, setEmpresas] = useState([]);
  const [paciente, setPaciente] = useState({
    nombre: '',
    email: '',
    password: '',
    rut: '',
    apellido_p: '',
    apellido_m: '',
    fecha_nac: '',
    direccion: '',
    telefono: '',
    id_empresa: '',
    sexo: '',
    genero: '',
  });
  const [originalPaciente, setOriginalPaciente] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();


  // Log de cada cambio (opcional)
  useEffect(() => {
    const fetchEmpresas = async () => {
      try {
        const { data } = await getCompanies();
        setEmpresas(data);
      } catch (error) {
        console.error('Error al obtener empresas:', error);
        toast.error('No se pudo cargar la lista de empresas.');
      }
    };
    fetchEmpresas();
  }, []);

  // Maneja inputs
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let formattedVal = value;
    if(name ==="rut"){
      formattedVal = formatRut(value);
    }else if(name ==="telefono"){
      formattedVal = formatPhone(value);
    }
    setPaciente((prev) => ({ ...prev, [name]: formattedVal }));
  };

  // Carga los datos del paciente al montar
  useEffect(() => {
    const fetchPatient = async () => {
      try {
        const { data } = await getPatientById(pacienteId);
        setPaciente({
          nombre: data.nombre || "",
          email: data.email || "",
          password: "",
          rut: data.rut ? formatRut(data.rut) : "",
          apellido_p: data.apellido_p || "",
          apellido_m: data.apellido_m || "",
          fecha_nac: data.fecha_nac || "",
          direccion: data.direccion || "",
          telefono: data.telefono || "",
          id_empresa: data.id_empresa || "",
          sexo: data.sexo || "",
          genero: data.genero || "",
        });

        setOriginalPaciente(data);
      } catch (error) {
        console.error("Error al cargar los datos del paciente:", error);
        setError("No se pudo cargar el paciente.");
      }
    };
    fetchPatient();
  }, [pacienteId]);


  // Envío de formulario usando el servicio
  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {};

    Object.keys(paciente).forEach(key => {
      let newVal = paciente[key];
      const oldVal = originalPaciente ? originalPaciente[key] : null;

      // contraseña: solo si se escribió algo
      if (key === "password") {
        if (newVal && newVal.trim() !== "") {
          payload[key] = newVal;
        }
        return;
      }
      if (key === "rut" && newVal){
        newVal = newVal.replace(/[^0-9kK]/g, '').toUpperCase();
      }
      // si cambió el valor
      if (newVal !== oldVal) {
        payload[key] = newVal === "" ? null : newVal;
      }
    });

    try {
      await patchPatient(pacienteId, payload);
      toast.success("Paciente actualizado exitosamente.");
      navigate("/pacientes");
    } catch (error) {
      console.error("Error al actualizar paciente:", error.response?.data || error);

      if (error.response?.status === 409) {
        // Conflicto de unicidad (email o rut)
        const details = error.response?.data?.details;
        if (details) {
          const msgs = Object.entries(details).map(([field, msg]) => `${field}: ${msg}`);
          toast.error(<div>Conflicto de unicidad:{msgs.map((m, i) => <div key={i}>{m}</div>)}</div>);
        } else {
          toast.error("Conflicto: RUT o email ya registrados.");
        }
      } else if (error.response?.status === 422) {
        // Errores de validación de Marshmallow
        const details = error.response?.data?.details;
        if (details) {
          const msgs = Object.entries(details).map(([field, errs]) => `${field}: ${Array.isArray(errs) ? errs.join(', ') : errs}`);
          toast.error(<div>Errores de validación:{msgs.map((m, i) => <div key={i}>{m}</div>)}</div>);
        } else {
          toast.error("Error de validación en los datos.");
        }
      } else {
        toast.error("Error inesperado al actualizar paciente.");
      }
    }
  };

  if (error) return <p className="required-text">{error}</p>;

  return (
    <div>
      <NavBar />
      <div className="content-wrapper">
        <h1>Editar Paciente</h1>
        <div className="container">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Nombre</label>
              <input
                type="text"
                name="nombre"
                value={paciente.nombre}
                onChange={handleInputChange}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Apellido Paterno</label>
              <input
                type="text"
                name="apellido_p"
                value={paciente.apellido_p}
                onChange={handleInputChange}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Apellido Materno</label>
              <input
                type="text"
                name="apellido_m"
                value={paciente.apellido_m}
                onChange={handleInputChange}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={paciente.email}
                onChange={handleInputChange}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Contraseña (dejar en blanco para no cambiar)</label>
              <input
                type="password"
                name="password"
                value={paciente.password}
                onChange={handleInputChange}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>RUT</label>
              <input
                type="text"
                name="rut"
                value={paciente.rut}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Ej: 12.345.678-K"
                maxLength="12"
              />
            </div>
            <div className="form-group">
              <label>Sexo</label>
              <select name="sexo" value={paciente.sexo} onChange={handleInputChange} className="form-control">
                <option value="">Seleccione sexo</option>
                <option value="femenino">Femenino</option>
                <option value="masculino">Masculino</option>
              </select>
            </div>

            <div className="form-group">
              <label>Identidad de Género</label>
              <select name="genero" value={paciente.genero} onChange={handleInputChange} className="form-control">
                <option value="">Seleccione género</option>
                <option value="mujer">Mujer</option>
                <option value="hombre">Hombre</option>
                <option value="no binario">No binario</option>
                <option value="no especificado">No especificado</option>
              </select>
            </div>
            <div className="form-group">
              <label>Fecha de Nacimiento</label>
              <input
                type="date"
                name="fecha_nac"
                value={paciente.fecha_nac}
                onChange={handleInputChange}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Dirección</label>
              <input
                type="text"
                name="direccion"
                value={paciente.direccion}
                onChange={handleInputChange}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Teléfono</label>
              <div className="phone-input-group">
                <span className="phone-prefix">+56</span>
                <input
                  type="tel"
                  name="telefono"
                  value={paciente.telefono}
                  onChange={handleInputChange}
                  maxLength={9}
                  size={16}
                  placeholder="912345678"
                  className="phone-input-field"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Empresa</label>
              <select
                name="id_empresa"
                value={paciente.id_empresa}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value=''>Seleccione una empresa</option>
                {empresas.map((empresa) => (
                  <option key={empresa.id_usuario} value={empresa.id_usuario}>
                    {empresa.nombre_legal}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <button type="submit" className="btn btn-create">
                Guardar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditPatient;